import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { AdminAlertDto } from '../data-transfer-objects/admin-alert.dto';

/**
 * The alerts bell's state, shared by every page's nav.
 *
 * Each page renders its own nav, so the list lives here rather than in the
 * component: moving between pages shows what is already known at once and
 * refreshes it behind. Dismissals are per admin on the server - dismissing
 * here leaves the alert in every other admin's bell.
 *
 * Only HttpClient here, so providing it in root is safe (see app.config.ts).
 */
@Injectable({ providedIn: 'root' })
export class AdminAlertsService {
  private readonly base = `${environment.baseURL}`;
  private readonly state = new BehaviorSubject<AdminAlertDto[]>([]);
  readonly alerts$: Observable<AdminAlertDto[]> = this.state.asObservable();

  constructor(private http: HttpClient) {}

  refresh(): void {
    this.http.get<{ alerts: AdminAlertDto[] }>(`${this.base}/alerts`).subscribe({
      next: (r) => this.state.next(r.alerts ?? []),
      // A bell that cannot load stays as it was; the boards still work.
      error: () => undefined,
    });
  }

  /** What this admin already dismissed, newest first. Fetched on demand, never polled. */
  dismissed(): Observable<AdminAlertDto[]> {
    return this.http
      .get<{ alerts: AdminAlertDto[] }>(`${this.base}/alerts`, { params: { dismissed: '1' } })
      .pipe(map((r) => r.alerts ?? []));
  }

  /**
   * Gone from the bell at once; put back if the server refuses, so a failed
   * dismiss never looks like a done one.
   */
  dismiss(ids: string[]): void {
    if (!ids.length) return;
    const before = this.state.value;
    this.state.next(before.filter((a) => !ids.includes(a.id)));
    this.http.post(`${this.base}/alerts/dismiss`, { ids }).subscribe({
      error: () => this.state.next(before),
    });
  }
}
